import React, { useCallback, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONSTANTS } from '../data.js';
import { ASSETS } from '../assetManifest.js';
import { iconFor, createDragPreviewElement, getCardStats } from '../lib/utils';
import type { CardInstance, TutorialState } from '../types';

type LibraryProps = {
  libraryRef: React.RefObject<HTMLDivElement>;
  overLibrary: boolean;
  elementsByEra: Record<string, string[]>;
  discovered: string[];
  onShowDetails: (key: string) => void;
  gamePhase: string;
  playerHand?: CardInstance[];
  battlePlayerDeck?: string[];
  onPassTurn: () => void;
  isPlayerTurn: boolean;
  playerHasPassed: boolean;
  bossHand?: CardInstance[];
  currentEraKey: string;
  tutorialState: TutorialState;
};

type SortMethod = 'era' | 'category' | 'name';

export const Library: React.FC<LibraryProps> = ({
  libraryRef,
  overLibrary,
  elementsByEra,
  discovered,
  onShowDetails,
  gamePhase,
  playerHand = [],
  battlePlayerDeck = [],
  onPassTurn,
  isPlayerTurn,
  playerHasPassed,
  currentEraKey,
  tutorialState,
}) => {
  if (!elementsByEra || typeof elementsByEra !== 'object') {
    throw new Error('Library: elementsByEra is missing or invalid.');
  }
  if (!Array.isArray(discovered)) {
    throw new Error('Library: discovered must be an array.');
  }

  const [sortMethod, setSortMethod] = useState<SortMethod>('era');
  const isBossBattle = gamePhase.startsWith('boss');

  const [draggedCardIndex, setDraggedCardIndex] = useState<number | null>(null);

  const initialCollapsed = useMemo(() => {
    const map: Record<string, boolean> = {};
    
    if (sortMethod === 'era') {
      for (const era of CONSTANTS.eras.list) {
        map[era.key] = false;
      }
    } else if (sortMethod === 'category') {
      for (const category of CONSTANTS.ui.library.categories) {
        map[category.key] = false;
      }
    }
    
    return map;
  }, [sortMethod]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(initialCollapsed);

  React.useEffect(() => {
    setCollapsed(initialCollapsed);
  }, [initialCollapsed]);

  const toggleSection = useCallback((sectionKey: string) => {
    setCollapsed(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const onPaletteDragStart = (e: React.DragEvent<HTMLButtonElement>, key: string) => {
    if (!key) {
      throw new Error('Library: drag start received empty element key.');
    }
    e.dataTransfer.setData('text/plain', key);
    e.dataTransfer.effectAllowed = 'copy';

    const ghost = createDragPreviewElement(key);
    e.dataTransfer.setDragImage(ghost, 36, 36);

    const cleanup = () => {
      ghost.remove();
    };
    e.currentTarget.addEventListener('dragend', cleanup, { once: true });
  };

  const handleCardDragStart = (e: React.DragEvent, index: number) => {
    setDraggedCardIndex(index);
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCardDragEnd = () => {
    setDraggedCardIndex(null);
  };

  const renderCard = (card: CardInstance, index: number) => {
    const { name, category } = CONSTANTS.elements[card.elementKey];
    const { power, description } = getCardStats(card, currentEraKey);
    const categoryInfo = CONSTANTS.ui.library.categories.find(c => c.key === category);
    const frameAssetKey = categoryInfo?.frameAsset || 'default';
    const framePath = ASSETS.card_frames?.[frameAssetKey]?.path;
    const categoryColor = categoryInfo?.color || CONSTANTS.ui.theme.colors.border;

    return (
      <motion.div
        layout
        key={card.id}
        draggable={isPlayerTurn && !playerHasPassed}
        onDragStart={(e) => handleCardDragStart(e, index)}
        onDragEnd={handleCardDragEnd}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className={`relative w-full h-48 rounded-lg p-2 flex flex-col bg-cover bg-center ${isPlayerTurn && !playerHasPassed ? 'cursor-grab active:cursor-grabbing' : 'cursor-not-allowed'}`}
        style={{
          backgroundImage: framePath ? `url(${framePath})` : `linear-gradient(145deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `1px solid ${categoryColor}`,
          boxShadow: `0 2px 6px rgba(0,0,0,0.3), 0 0 5px ${categoryColor}30`,
          opacity: draggedCardIndex === index ? 0.5 : 1,
        }}
      >
        <div className="flex justify-between items-start mb-1">
          <span className="font-cinzel text-sm font-bold text-white/90 leading-tight line-clamp-2 flex-1">{name}</span>
          <span className="font-bold text-lg ml-1" style={{color: CONSTANTS.ui.theme.colors.primary}}>{power}</span>
        </div>
        <div className="w-full h-20 bg-black/20 rounded-md my-1 flex items-center justify-center overflow-hidden flex-shrink-0">
          <img src={iconFor(card.elementKey)} alt={name} className="w-16 h-16 object-contain" />
        </div>
        <div className="flex-grow flex flex-col justify-between mt-1">
          <p className="font-crimson text-[11px] text-white/70 leading-tight line-clamp-3">{description}</p>
          <div className="font-cinzel text-[10px] text-white/50 capitalize text-right">{category}</div>
        </div>
      </motion.div>
    );
  };

  const buildEraHeaderStyle = (eraKey: string) => {
    const headerCfg = CONSTANTS.ui?.library?.eraHeader || {};
    const eraStyle = (CONSTANTS.ui?.library?.eraStyles || {})[eraKey] || {};
    const angle = typeof headerCfg.angleDeg === 'number' ? headerCfg.angleDeg : 135;
    const height = typeof headerCfg.height === 'number' ? headerCfg.height : 68;
    const radius = typeof headerCfg.borderRadius === 'number' ? headerCfg.borderRadius : 10;

    const from = typeof eraStyle.gradientFrom === 'string' ? eraStyle.gradientFrom : '#111827';
    const to = typeof eraStyle.gradientTo === 'string' ? eraStyle.gradientTo : '#1f2937';

    const bgPath = ASSETS.backgrounds?.[eraKey]?.path;

    const gradientLayer = `linear-gradient(${angle}deg, ${from} 0%, ${to} 100%)`;
    const backgroundImage = bgPath ? `${gradientLayer}, url(${bgPath})` : gradientLayer;

    return {
      style: {
        backgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        height: `${height}px`,
        borderRadius: `${radius}px`,
      } as React.CSSProperties
    };
  };

  const buildCategoryHeaderStyle = (categoryKey: string) => {
    const headerCfg = CONSTANTS.ui?.library?.eraHeader || {};
    const category = CONSTANTS.ui.library.categories.find(c => c.key === categoryKey);
    const angle = typeof headerCfg.angleDeg === 'number' ? headerCfg.angleDeg : 135;
    const height = typeof headerCfg.height === 'number' ? headerCfg.height : 68;
    const radius = typeof headerCfg.borderRadius === 'number' ? headerCfg.borderRadius : 10;

    const color = category?.color || '#64748b';
    const gradientLayer = `linear-gradient(${angle}deg, ${color}40 0%, ${color}80 100%)`;

    return {
      style: {
        backgroundImage: gradientLayer,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        height: `${height}px`,
        borderRadius: `${radius}px`,
      } as React.CSSProperties
    };
  };

  const elementsByCategory = useMemo(() => {
    const map: Record<string, string[]> = {};
    
    for (const category of CONSTANTS.ui.library.categories) {
      map[category.key] = [];
    }
    
    for (const key of Object.keys(CONSTANTS.elements)) {
      const element = CONSTANTS.elements[key];
      const categoryKey = element.category || 'materials';
      if (!map[categoryKey]) map[categoryKey] = [];
      map[categoryKey].push(key);
    }
    
    for (const categoryKey of Object.keys(map)) {
      map[categoryKey].sort((a, b) => CONSTANTS.elements[a].name.localeCompare(CONSTANTS.elements[b].name));
    }
    
    return map;
  }, []);

  const elementsByName = useMemo(() => {
    return Object.keys(CONSTANTS.elements).sort((a, b) => CONSTANTS.elements[a].name.localeCompare(CONSTANTS.elements[b].name));
  }, []);

  const renderElementGrid = (elementKeys: string[]) => {
    const unlocked = elementKeys.filter(k => discovered.includes(k));
    const lockedCount = elementKeys.length - unlocked.length;

    return (
      <div className="grid grid-cols-4 gap-2">
        {unlocked.map(k => {
          const el = CONSTANTS.elements[k];
          if (!el) {
            throw new Error(`Library: element key "${k}" missing from CONSTANTS.elements.`);
          }
          return (
            <button
              key={k}
              draggable
              onDragStart={(e) => onPaletteDragStart(e, k)}
              onClick={() => onShowDetails(k)}
              className="font-crimson group rounded-lg p-1.5 flex flex-col items-center cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary}10 0%, ${CONSTANTS.ui.theme.colors.accent}10 100%)`,
                border: `1px solid ${CONSTANTS.ui.theme.colors.border}40`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              title={el.name}
              data-tutorial-target={k === 'stone' ? 'library_stone' : undefined}
            >
              <img
                src={iconFor(k)}
                alt={el.name}
                className="w-12 h-12 object-contain"
              />
              <span 
                className="mt-1 text-[10px] text-center leading-tight"
                style={{ color: CONSTANTS.ui.theme.colors.text }}
              >
                {el.name}
              </span>
            </button>
          );
        })}
        {Array.from({ length: lockedCount }).map((_, idx) => (
          <div
            key={idx}
            className="rounded-lg p-1.5 flex flex-col items-center opacity-50"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface}40 0%, ${CONSTANTS.ui.theme.colors.background}40 100%)`,
              border: `1px solid ${CONSTANTS.ui.theme.colors.border}20`
            }}
          >
            <img
              src={iconFor('default')}
              alt="Unknown"
              className="w-12 h-12 object-contain"
            />
            <span 
              className="mt-1 text-[10px]"
              style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
            >
              ???
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderByEra = () => {
    return CONSTANTS.eras.list.map((era) => {
      const list = elementsByEra[era.key] || [];
      const unlocked = list.filter(k => discovered.includes(k));

      if (list.length === 0) return null;

      const isCollapsed = !!collapsed[era.key];
      const { style } = buildEraHeaderStyle(era.key);
      const accent = era.color || '#ffffff';

      return (
        <div 
          key={era.key} 
          className="px-3 py-3"
          style={{ borderBottom: `1px solid ${CONSTANTS.ui.theme.colors.border}40` }}
        >
          <div className="mb-2">
            <button
              type="button"
              onClick={() => toggleSection(era.key)}
              className="w-full relative overflow-hidden group text-left transition-all duration-200 hover:scale-[1.02]"
              aria-expanded={!isCollapsed}
              aria-controls={`era-section-${era.key}`}
              title={isCollapsed ? 'Expand' : 'Collapse'}
              style={style}
            >
              <div
                className="absolute left-0 top-0 bottom-0"
                style={{ width: '4px', backgroundColor: accent }}
              />
              <div 
                className="absolute inset-0 transition-colors duration-200"
                style={{ background: 'rgba(26, 22, 17, 0.3)' }}
              />
              <div className="relative z-10 h-full flex items-center justify-between px-3 sm:px-4">
                <div className="flex items-center gap-3">
                  <span
                    className="font-cinzel text-lg sm:text-xl font-semibold"
                    style={{ 
                      color: CONSTANTS.ui.theme.colors.text,
                      textShadow: '0 1px 6px rgba(0,0,0,0.45)'
                    }}
                  >
                    {era.name}
                  </span>
                  <span 
                    className="font-crimson text-sm hidden sm:inline"
                    style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                  >
                    {isCollapsed ? '▸' : '▾'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="font-crimson text-[11px] sm:text-xs px-2 py-1 rounded-md"
                    style={{
                      border: `1px solid ${CONSTANTS.ui.theme.colors.border}40`,
                      background: 'rgba(26, 22, 17, 0.35)',
                      color: CONSTANTS.ui.theme.colors.text
                    }}
                  >
                    {unlocked.length} / {list.length}
                  </span>
                </div>
              </div>
            </button>
          </div>

          {!isCollapsed && (
            <div id={`era-section-${era.key}`}>
              {renderElementGrid(list)}
            </div>
          )}
        </div>
      );
    });
  };

  const renderByCategory = () => {
    return CONSTANTS.ui.library.categories.map((category) => {
      const list = elementsByCategory[category.key] || [];
      const unlocked = list.filter(k => discovered.includes(k));

      if (list.length === 0) return null;

      const isCollapsed = !!collapsed[category.key];
      const { style } = buildCategoryHeaderStyle(category.key);
      const accent = category.color || '#ffffff';

      return (
        <div 
          key={category.key} 
          className="px-3 py-3"
          style={{ borderBottom: `1px solid ${CONSTANTS.ui.theme.colors.border}40` }}
        >
          <div className="mb-2">
            <button
              type="button"
              onClick={() => toggleSection(category.key)}
              className="w-full relative overflow-hidden group text-left transition-all duration-200 hover:scale-[1.02]"
              aria-expanded={!isCollapsed}
              aria-controls={`category-section-${category.key}`}
              title={isCollapsed ? 'Expand' : 'Collapse'}
              style={style}
            >
              <div
                className="absolute left-0 top-0 bottom-0"
                style={{ width: '4px', backgroundColor: accent }}
              />
              <div 
                className="absolute inset-0 transition-colors duration-200"
                style={{ background: 'rgba(26, 22, 17, 0.3)' }}
              />
              <div className="relative z-10 h-full flex items-center justify-between px-3 sm:px-4">
                <div className="flex items-center gap-3">
                  <span
                    className="font-cinzel text-lg sm:text-xl font-semibold"
                    style={{ 
                      color: CONSTANTS.ui.theme.colors.text,
                      textShadow: '0 1px 6px rgba(0,0,0,0.45)'
                    }}
                  >
                    {category.name}
                  </span>
                  <span 
                    className="font-crimson text-sm hidden sm:inline"
                    style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                  >
                    {isCollapsed ? '▸' : '▾'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="font-crimson text-[11px] sm:text-xs px-2 py-1 rounded-md"
                    style={{
                      border: `1px solid ${CONSTANTS.ui.theme.colors.border}40`,
                      background: 'rgba(26, 22, 17, 0.35)',
                      color: CONSTANTS.ui.theme.colors.text
                    }}
                  >
                    {unlocked.length} / {list.length}
                  </span>
                </div>
              </div>
            </button>
          </div>

          {!isCollapsed && (
            <div id={`category-section-${category.key}`}>
              {renderElementGrid(list)}
            </div>
          )}
        </div>
      );
    });
  };

  const renderByName = () => {
    const unlocked = elementsByName.filter(k => discovered.includes(k));
    const locked = elementsByName.filter(k => !discovered.includes(k));

    return (
      <div className="px-3 py-3">
        <div className="mb-4">
          <div 
            className="font-crimson text-sm mb-2"
            style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
          >
            Discovered Elements ({unlocked.length} / {elementsByName.length})
          </div>
          {renderElementGrid([...unlocked, ...locked])}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={libraryRef}
      className={`${isBossBattle ? 'w-[320px]' : 'w-[420px]'} h-full overflow-auto transition-all duration-300 ease-in-out`}
      style={{
        background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.background} 0%, ${CONSTANTS.ui.theme.colors.surface} 100%)`,
        borderLeft: overLibrary 
          ? `2px solid ${CONSTANTS.ui.theme.colors.primary}` 
          : `2px solid ${CONSTANTS.ui.theme.colors.border}`,
        boxShadow: overLibrary 
          ? `inset 4px 0 8px rgba(218, 165, 32, 0.2)` 
          : 'inset 2px 0 4px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div 
        className="p-3"
        style={{ borderBottom: `1px solid ${CONSTANTS.ui.theme.colors.border}40` }}
      >
        <div 
          className="font-cinzel text-lg font-bold mb-2"
          style={{ color: CONSTANTS.ui.theme.colors.primary }}
        >
          {isBossBattle ? 'Battle Cards' : 'Alchemical Library'}
        </div>
        
        {!isBossBattle && (
          <>
            <div 
              className="font-crimson text-xs mb-3 leading-relaxed"
              style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
            >
              Drag an element to the board to spawn it, or click to view its recipes.
            </div>
            
            <div className="mb-2">
              <div 
                className="font-crimson text-xs mb-1"
                style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
              >
                Sort by:
              </div>
              <select
                value={sortMethod}
                onChange={(e) => setSortMethod(e.target.value as SortMethod)}
                className="font-crimson w-full px-2 py-1 text-xs rounded focus:outline-none transition-colors duration-200"
                style={{
                  background: CONSTANTS.ui.theme.colors.surface,
                  border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
                  color: CONSTANTS.ui.theme.colors.text
                }}
              >
                {CONSTANTS.ui.library.sortMethods.map(method => (
                  <option key={method.key} value={method.key}>
                    {method.name}
                  </option>
                ))}
              </select>
            </div>

            {overLibrary && (
              <div 
                className="font-crimson mt-2 text-xs px-2 py-1 rounded"
                style={{ 
                  color: CONSTANTS.ui.theme.colors.primary,
                  background: `${CONSTANTS.ui.theme.colors.primary}20`,
                  border: `1px solid ${CONSTANTS.ui.theme.colors.primary}40`
                }}
              >
                Release to store element in library
              </div>
            )}
          </>
        )}

        {isBossBattle && (
          <div 
            className="font-crimson text-xs mb-3 leading-relaxed"
            style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
          >
            Drag a card to the field to play it. You can also pass your turn.
          </div>
        )}
      </div>

      {!isBossBattle && (
        <div>
          {sortMethod === 'era' && renderByEra()}
          {sortMethod === 'category' && renderByCategory()}
          {sortMethod === 'name' && renderByName()}
        </div>
      )}

      {isBossBattle && (
        <div className="p-3">
          <div className="mb-6">
            <div 
              className="font-cinzel text-md font-bold mb-3 flex justify-between items-center"
              style={{ color: CONSTANTS.ui.theme.colors.success }}
              data-tutorial-target="player_hand"
            >
              <span>Your Hand ({playerHand.length})</span>
              <div className="flex items-center gap-2" title={`${battlePlayerDeck.length} cards left in deck`}>
                <img src={iconFor('deck')} alt="Deck" className="w-6 h-6" />
                <span className="font-crimson text-sm">{battlePlayerDeck.length}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
              <AnimatePresence>
                {playerHand.map((card, i) => renderCard(card, i))}
              </AnimatePresence>
            </div>
            
            <div className="flex gap-2 mt-4">
              <button 
                onClick={onPassTurn} 
                disabled={!isPlayerTurn || playerHasPassed || gamePhase !== 'boss_playing'}
                className="font-crimson flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: isPlayerTurn && !playerHasPassed && gamePhase === 'boss_playing'
                    ? `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`
                    : CONSTANTS.ui.theme.colors.surface,
                  color: isPlayerTurn && !playerHasPassed && gamePhase === 'boss_playing' ? CONSTANTS.ui.theme.colors.background : CONSTANTS.ui.theme.colors.textSecondary,
                  border: `1px solid ${CONSTANTS.ui.theme.colors.border}`
                }}
                data-tutorial-target="pass_button"
              >
                Pass Turn
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
